import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import moment from 'moment'
import {
  Paper, Tabs, Tab, Button, Switch, Select, MenuItem, FormControl,
  InputLabel, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Badge, Snackbar, CircularProgress, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import AddTaskIcon from '@mui/icons-material/AddTask'
import AddIcon from '@mui/icons-material/Add'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import BlockIcon from '@mui/icons-material/Block'
import ScheduleIcon from '@mui/icons-material/Schedule'
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import PeriodSelector from '../shared/report/PeriodSelector'
import KpiCard from '../shared/report/KpiCard'
import ReportChart from '../shared/report/ReportChart'
import { periodToRange, daysBetween, periodLabelOf } from '../shared/report/periodRange'
import Text from '../Text'
import RuleEditor from './RuleEditor'
import SuppressionList from './SuppressionList'
import AllowList from './AllowList'
import Decisions from './Decisions'
import OutcomeChip from './OutcomeChip'
import RecipeInstaller from './RecipeInstaller'
import SilencedCustomers from './SilencedCustomers'
import BudgetDialog from './BudgetDialog'
import { labelReason, labelSource } from './labels'

const sf = hipersendService.smartFilter

const MODE_LABELS = {
  off: 'Desligado',
  shadow: 'Observação (shadow)',
  simulation: 'Simulação',
  active: 'Ativo'
}

const DECISION_COLORS = {
  ALLOW: 'success',
  PASS_THROUGH: 'default',
  BLOCK: 'error',
  DELAY: 'warning',
  REQUIRE_APPROVAL: 'secondary',
  TRANSFORM: 'info',
  TAG_ONLY: 'default'
}

export default function SmartFilter() {
  const theme = useTheme()
  const { getCredentials, user, usaLinhaDeCredito, precoMsgPacote, refreshSaldo } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()

  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const [settings, setSettings] = useState(null)
  const [stats, setStats] = useState(null)
  const [ranking, setRanking] = useState([])
  const [heatmap, setHeatmap] = useState({ hours: Array(24).fill(0) })
  const [templates, setTemplates] = useState([])
  const [rules, setRules] = useState([])
  const [approvals, setApprovals] = useState([])
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [recipeToInstall, setRecipeToInstall] = useState(null)
  const [budget, setBudget] = useState({ data: [], costBRL: 0.04 })
  const [budgetDialog, setBudgetDialog] = useState({ open: false, rule: null })
  const [resetOpen, setResetOpen] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [resetDate, setResetDate] = useState('')
  const [instanceStats, setInstanceStats] = useState(null)

  // Período do relatório (aba Visão geral). Presets em BRT, reaproveitados do
  // relatório de envios (módulo compartilhado).
  const [period, setPeriod] = useState('30d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const selectedRange = useMemo(() => periodToRange(period, customStart, customEnd), [period, customStart, customEnd])
  const [applied, setApplied] = useState(() => ({ range: selectedRange, period: '30d' }))
  const rangeRef = useRef(applied.range)
  useEffect(() => { rangeRef.current = applied.range }, [applied])
  const periodLabel = useMemo(
    () => periodLabelOf(applied.period, applied.range.startDay, applied.range.endDayInclusive, moment),
    [applied]
  )
  const [autoRefresh, setAutoRefresh] = useState(false)
  const autoTickRef = useRef(false)
  const [reportLoading, setReportLoading] = useState(true)

  const hasAccess = user?.plan?.functionalities?.includes('SmartFilter')

  const notify = (msg) => setMessage(msg)

  const loadSettings = useCallback(async () => {
    const data = await sf.getSettings(api, secret)
    setSettings(data || {})
  }, [api, secret])

  // Os 3 loaders do relatório leem o período corrente (rangeRef) e mandam
  // dateBegin/dateEnd. O backend agrupa em BRT; sem range, cai no fallback `days`.
  const loadStats = useCallback(async () => {
    const { dateBegin, dateEnd } = rangeRef.current || {}
    const data = await sf.getStats(api, secret, { dateBegin, dateEnd })
    setStats(data || { totals: {}, byDay: [] })
  }, [api, secret])

  const loadRanking = useCallback(async () => {
    const { dateBegin, dateEnd } = rangeRef.current || {}
    const data = await sf.getRuleRanking(api, secret, { dateBegin, dateEnd })
    setRanking(data || [])
  }, [api, secret])

  const loadHeatmap = useCallback(async () => {
    const { dateBegin, dateEnd } = rangeRef.current || {}
    const data = await sf.getHourHeatmap(api, secret, { dateBegin, dateEnd })
    setHeatmap(data || { hours: Array(24).fill(0) })
  }, [api, secret])

  const loadBudget = useCallback(async () => {
    const res = await sf.getBudget(api, secret)
    setBudget({ data: (res && res.data) || [], costBRL: res && res.costBRL != null ? res.costBRL : 0.04 })
  }, [api, secret])

  const loadInstanceStats = useCallback(async () => {
    try {
      const data = await hipersendService.notificacoesInteligentes.getMessagesStatistics(api, secret)
      setInstanceStats(data || null)
    } catch (e) {
      console.error(e)
    }
  }, [api, secret])

  const loadTemplates = useCallback(async () => {
    setTemplates(await sf.listTemplates(api, secret))
  }, [api, secret])

  const loadRules = useCallback(async () => {
    setRules(await sf.listRules(api, secret))
  }, [api, secret])

  const loadApprovals = useCallback(async () => {
    setApprovals(await sf.listApprovals(api, secret, { status: 'pending' }))
  }, [api, secret])

  const loadTab = useCallback(async (current) => {
    if (!hasAccess) return
    if (current === 1 || current === 8) return // Decisões e Silenciados carregam sozinhas
    setLoading(true)
    try {
      if (current === 0) await Promise.all([loadBudget(), loadSettings(), loadInstanceStats(), refreshSaldo()])
      else if (current === 2) await loadSettings()
      else if (current === 3) { await loadTemplates(); await loadRules() }
      else if (current === 4) await loadRules()
      else if (current === 5) await loadRules()
      else if (current === 6) { await loadRules(); await loadSettings() }
      else if (current === 7) await loadApprovals()
    } catch (e) {
      notify('Falha ao carregar dados do SmartFilter')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [hasAccess, loadStats, loadBudget, loadSettings, loadTemplates, loadRules, loadApprovals, loadRanking, loadHeatmap, loadInstanceStats, refreshSaldo])

  useEffect(() => {
    loadTab(tab)
  }, [tab, loadTab])

  // Relatório (stats/ranking/heatmap): recarrega ao entrar na Visão geral ou ao
  // mudar o período. Custom incompleto (só uma data) espera. Skeleton via
  // reportLoading — não usa o spinner cheio da aba.
  useEffect(() => {
    if (!hasAccess || !api || !secret || tab !== 0 || selectedRange.incomplete) return
    // rangeRef sincroniza JÁ (os loaders buscam o período novo); mas `applied`
    // — que dá o eixo e o rótulo do gráfico — só avança quando os dados novos
    // chegam, pra não renderizar o eixo do período novo contra o byDay do anterior.
    rangeRef.current = selectedRange
    setReportLoading(true)
    Promise.all([loadStats(), loadRanking(), loadHeatmap()])
      .catch(() => {})
      .finally(() => {
        setApplied({ range: selectedRange, period })
        setReportLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedRange.dateBegin, selectedRange.dateEnd, api, secret, hasAccess])

  // Auto-refresh 10s na Visão geral: pausa em aba oculta, não sobrepõe chamadas,
  // silencioso (não pisca skeleton) e limpo ao desligar/desmontar/trocar de aba.
  useEffect(() => {
    if (!autoRefresh || !hasAccess || !api || !secret || tab !== 0) return
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      if (autoTickRef.current) return
      autoTickRef.current = true
      try {
        await Promise.all([loadStats(), loadRanking(), loadHeatmap(), loadInstanceStats(), refreshSaldo()])
      } catch (e) {
        // silencioso — a próxima batida tenta de novo
      } finally {
        autoTickRef.current = false
      }
    }
    const id = setInterval(tick, 10000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, hasAccess, api, secret, tab])

  // Carrega a contagem de aprovações pendentes na montagem, pro badge da aba
  // já mostrar o número antes mesmo de abrir a aba.
  useEffect(() => {
    if (hasAccess) loadApprovals().catch(() => {})
  }, [hasAccess, loadApprovals])

  // ----- ações -----
  const saveSettings = async () => {
    setLoading(true)
    try {
      const data = await sf.updateSettings(api, secret, settings)
      setSettings(data)
      notify('Configurações salvas')
    } catch (e) {
      notify('Falha ao salvar configurações')
    } finally {
      setLoading(false)
    }
  }

  const toggleRule = async (id) => {
    try {
      await sf.toggleRule(api, secret, id)
      await loadRules()
    } catch (e) {
      notify('Falha ao alterar a regra')
    }
  }

  const confirmDeleteRule = async () => {
    try {
      await sf.deleteRule(api, secret, ruleToDelete._id)
      setRuleToDelete(null)
      await loadRules()
      notify('Regra removida')
    } catch (e) {
      notify('Falha ao remover a regra')
    }
  }

  const decideApproval = async (id, approve) => {
    try {
      if (approve) await sf.approveApproval(api, secret, id)
      else await sf.rejectApproval(api, secret, id)
      await loadApprovals()
      notify(approve ? 'Mensagem aprovada e enviada' : 'Mensagem rejeitada')
    } catch (e) {
      notify('Falha ao processar a aprovação')
    }
  }

  // Garante que existe (e está ativa) a regra que bloqueia a lista de supressão.
  const ensureSuppressionRule = useCallback(async () => {
    let r = rules.find((x) => x.templateCode === 'lista-supressao' || (x.conditions?.items || []).some((it) => it.value === 'suppressionList'))
    if (!r) r = await sf.importTemplate(api, secret, 'lista-supressao')
    if (r && !r.enabled) await sf.toggleRule(api, secret, r._id)
    await loadRules()
  }, [rules, api, secret, loadRules])

  // Garante que existe (e está ativa) a regra que libera a lista de sempre enviar.
  const ensureVipRule = useCallback(async () => {
    let r = rules.find((x) => x.templateCode === 'vip-sempre' || (x.conditions?.items || []).some((it) => it.value === 'vipList'))
    if (!r) r = await sf.importTemplate(api, secret, 'vip-sempre')
    if (r && !r.enabled) await sf.toggleRule(api, secret, r._id)
    await loadRules()
  }, [rules, api, secret, loadRules])

  // Garante que existe (e está ativa) a garantia "Desbloqueio sempre passa",
  // para reativação furar o bloqueio de inadimplência antiga.
  const ensureUnblockGuarantee = useCallback(async () => {
    const fresh = await sf.listRules(api, secret)
    let r = fresh.find((x) => x.templateCode === 'desbloqueio-sempre' ||
      (x.action?.type === 'ALLOW' && (x.conditions?.items || []).some((it) => it.path === 'facts.intent' && it.value === 'desbloqueio')))
    if (!r) r = await sf.importTemplate(api, secret, 'desbloqueio-sempre')
    if (r && !r.enabled) await sf.toggleRule(api, secret, r._id)
    await loadRules()
  }, [api, secret, loadRules])

  const confirmReset = async () => {
    setResetting(true)
    try {
      const opts = resetDate ? { deleteBefore: new Date(resetDate + 'T00:00:00').toISOString() } : {}
      const result = await sf.resetData(api, secret, opts)
      const total = result
        ? Object.values(result).reduce((s, n) => s + (n || 0), 0)
        : 0
      setResetOpen(false)
      setResetDate('')
      const label = resetDate
        ? `Dados anteriores a ${new Date(resetDate + 'T12:00:00').toLocaleDateString('pt-BR')} removidos — ${total.toLocaleString('pt-BR')} registro(s)`
        : `Dados resetados — ${total.toLocaleString('pt-BR')} registro(s) removido(s)`
      notify(label)
      // recarrega a aba atual pra refletir o estado limpo
      await loadTab(tab)
    } catch (e) {
      notify('Falha ao resetar os dados do SmartFilter')
    } finally {
      setResetting(false)
    }
  }

  // ----- gates -----
  // Redireciona pro home se o plano não inclui SmartFilter. Sem isso, um usuário
  // pode acessar /smart-filter direto pela URL mesmo sem o menu aparecer.
  if (user && !hasAccess) {
    window.location.href = '/'
    return null
  }

  if (!user) {
    return (
      <div className="flex justify-center p-12">
        <CircularProgress />
      </div>
    )
  }

  const installedCodes = new Set(rules.map((r) => r.templateCode).filter(Boolean))

  return (
    <div className="p-4">
      <div className="flex items-center space-x-2 mb-4">
        <FilterAltIcon style={{ color: theme.palette.primary.main }} />
        <Text className="text-xl font-bold">SmartFilter</Text>
      </div>

      <Paper variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52 } }}
        >
          <Tab label="Visão geral" />
          <Tab label="Decisões" />
          <Tab label="Configurações" />
          <Tab label="Receitas" />
          <Tab label="Regras" />
          <Tab label="Lista de bloqueio" />
          <Tab label="Lista de sempre enviar" />
          <Tab label={approvals.length ? (
            <Badge color="secondary" badgeContent={approvals.length} max={999}
              sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', ml: 1 } }}>
              Aprovações
            </Badge>
          ) : 'Aprovações'} />
          <Tab label="Silenciados" />
        </Tabs>

        <div className="p-5">
          {loading && tab !== 0 && (
            <div className="flex justify-center py-6"><CircularProgress size={28} /></div>
          )}

          {tab === 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <PeriodSelector
                  period={period}
                  customStart={customStart}
                  customEnd={customEnd}
                  incomplete={selectedRange.incomplete}
                  onChange={({ period: p, customStart: cs, customEnd: ce }) => {
                    setPeriod(p); setCustomStart(cs || ''); setCustomEnd(ce || '')
                  }}
                />
                <label className="flex items-center gap-1 cursor-pointer select-none shrink-0">
                  <Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                  <AutorenewIcon style={{ fontSize: 16, color: autoRefresh ? theme.palette.primary.main : 'currentColor', opacity: autoRefresh ? 1 : 0.5 }} />
                  <Text className="text-xs font-medium opacity-70">Atualizar a cada 10s</Text>
                </label>
              </div>
              {loading ? (
                <div className="flex justify-center py-6"><CircularProgress size={28} /></div>
              ) : (
                <Overview
                  stats={stats}
                  theme={theme}
                  budget={budget.data}
                  costBRL={budget.costBRL}
                  mode={settings?.mode}
                  ranking={ranking}
                  heatmap={heatmap}
                  instanceStats={instanceStats}
                  usaLinhaDeCredito={usaLinhaDeCredito}
                  periodLabel={periodLabel}
                  reportLoading={reportLoading}
                  onNewBudget={() => setBudgetDialog({ open: true, rule: null })}
                  onEditBudget={(b) => setBudgetDialog({ open: true, rule: budgetSummaryToRule(b) })}
                />
              )}
            </div>
          )}
          {tab === 1 && <Decisions api={api} secret={secret} defaultSource={settings?.defaultSource} />}
          {!loading && tab === 2 && settings && (
            <SettingsTab settings={settings} setSettings={setSettings} onSave={saveSettings} onReset={() => setResetOpen(true)} />
          )}
          {!loading && tab === 3 && (
            <TemplatesTab
              templates={templates}
              installedCodes={installedCodes}
              onImport={(t) => (t.code === 'teto-envios-periodo'
                ? setBudgetDialog({ open: true, rule: null })
                : setRecipeToInstall(t))}
            />
          )}
          {!loading && tab === 4 && (
            <RulesTab
              rules={rules}
              onToggle={toggleRule}
              onDelete={setRuleToDelete}
              onNew={() => { setEditingRule(null); setEditorOpen(true) }}
              onEdit={(r) => { setEditingRule(r); setEditorOpen(true) }}
            />
          )}
          {!loading && tab === 5 && (
            <SuppressionList api={api} secret={secret} rules={rules} onEnsureRule={ensureSuppressionRule} notify={notify} />
          )}
          {!loading && tab === 6 && (
            <AllowList api={api} secret={secret} rules={rules} filterMode={settings?.filterMode} onEnsureRule={ensureVipRule} notify={notify} />
          )}
          {!loading && tab === 7 && (
            <ApprovalsTab approvals={approvals} onDecide={decideApproval} />
          )}
          {tab === 8 && <SilencedCustomers api={api} secret={secret} settings={settings} />}
        </div>
      </Paper>

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={loadRules}
        api={api}
        secret={secret}
        rule={editingRule}
      />

      <RecipeInstaller
        template={recipeToInstall}
        api={api}
        secret={secret}
        onClose={() => setRecipeToInstall(null)}
        onDone={async () => {
          await loadRules()
          if (recipeToInstall?.code === 'parar-cobranca-inadimplencia-antiga') await ensureUnblockGuarantee()
        }}
        notify={notify}
      />

      <BudgetDialog
        open={budgetDialog.open}
        rule={budgetDialog.rule}
        costBRL={budget.costBRL}
        api={api}
        secret={secret}
        onClose={() => setBudgetDialog({ open: false, rule: null })}
        onSaved={async () => { await loadRules(); await loadBudget() }}
        notify={notify}
        usaLinhaDeCredito={usaLinhaDeCredito}
        precoMsgPacote={precoMsgPacote}
      />

      <Dialog open={!!ruleToDelete} onClose={() => setRuleToDelete(null)} PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Remover regra</DialogTitle>
        <DialogContent>
          <Text>Tem certeza que deseja remover a regra "{ruleToDelete?.name}"?</Text>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRuleToDelete(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button color="error" variant="contained" disableElevation onClick={confirmDeleteRule} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>Remover</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => { if (!resetting) { setResetOpen(false); setResetDate('') } }} PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Resetar dados do SmartFilter</DialogTitle>
        <DialogContent>
          <Text className="block mb-2">
            Apaga dados acumulados desta instância (inadimplência, bloqueios, decisões, contadores, aprovações).
          </Text>
          <Text className="block mt-3 text-sm font-semibold">
            As regras, receitas e configurações serão preservadas.
          </Text>

          <div className="mt-5 p-3 rounded-xl" style={{ border: '1px solid', borderColor: 'rgba(128,128,128,0.25)', background: 'rgba(128,128,128,0.04)' }}>
            <Text className="block text-sm font-semibold mb-2">Limitar por data (opcional)</Text>
            <Text className="block text-xs opacity-60 mb-3">
              Apaga apenas dados <strong>anteriores</strong> à data selecionada. Dados a partir dessa data são preservados. Se não selecionar, apaga tudo.
            </Text>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={resetDate}
              onChange={(e) => setResetDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              label="Apagar dados anteriores a"
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            />
            {resetDate && (
              <Text className="block text-xs mt-2" style={{ color: '#f59e0b' }}>
                Dados do dia {new Date(resetDate + 'T12:00:00').toLocaleDateString('pt-BR')} em diante serão mantidos.
              </Text>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setResetOpen(false); setResetDate('') }} disabled={resetting} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button color="error" variant="contained" disableElevation disabled={resetting} onClick={confirmReset}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>
            {resetting ? 'Resetando…' : resetDate ? `Apagar antes de ${new Date(resetDate + 'T12:00:00').toLocaleDateString('pt-BR')}` : 'Resetar tudo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        message={message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  )
}

// =========================================================================
// Abas
// =========================================================================

function StatCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: '16px', p: 2.5, transition: 'box-shadow .2s', '&:hover': { boxShadow: 2 } }}>
      <Text className="block text-xs uppercase opacity-55" style={{ letterSpacing: '0.06em' }}>{label}</Text>
      <Text className="block text-2xl font-bold mt-1" style={{ color }}>{value}</Text>
    </Paper>
  )
}

// Reconstroi uma "regra" minima a partir do resumo do /budget, para o editor.
function budgetSummaryToRule(b) {
  return {
    _id: b.ruleId,
    name: b.ruleName,
    enabled: b.enabled,
    action: { type: b.action },
    conditions: { op: 'AND', items: [{ type: 'SEND_BUDGET', period: b.period, operator: 'gte', value: b.limit }] }
  }
}

function isoToBr(s) {
  if (!s) return ''
  const [y, m, d] = String(s).slice(0, 10).split('-')
  return y && m && d ? `${d}/${m}/${y}` : s
}
function fmtBRL(n) {
  return Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
function periodLabel(p) {
  if (!p) return ''
  if (p.type === 'monthly') {
    const d = p.from ? new Date(p.from) : new Date()
    return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  }
  return `${isoToBr(p.from)} → ${isoToBr(p.to)}`
}
// Projeta quando o teto seria atingido no ritmo atual (null se nao estoura na janela).
function budgetProjection(b) {
  try {
    if (!b.enabled || !b.period) return null
    const from = b.period.from ? new Date(b.period.from) : null
    const to = b.period.to ? new Date(b.period.to) : null
    if (!from || !to) return null
    const now = new Date()
    const elapsed = now - from
    const remaining = (b.limit || 0) - (b.count || 0)
    if (elapsed <= 0 || !b.count || remaining <= 0) return null
    const eta = new Date(now.getTime() + (remaining * elapsed) / b.count)
    if (eta > to) return null
    return eta.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  } catch (e) {
    return null
  }
}

function BudgetMeter({ b, onEdit, showCost }) {
  // Preço/msg real do pacote contratado (fallback no custo fixo legado).
  const { precoMsgPacote } = useContext(ApiDataWrapperContext)
  const price = precoMsgPacote || PRICE_PER_MESSAGE
  const limit = b.limit || 0
  const pct = limit > 0 ? Math.min(100, Math.round(((b.count || 0) / limit) * 100)) : 0
  const over = limit > 0 && (b.count || 0) >= limit
  const remaining = Math.max(0, limit - (b.count || 0))
  const color = !b.enabled ? '#98a2b3' : over ? '#f04438' : pct >= 80 ? '#f79009' : '#12b76a'
  const eta = budgetProjection(b)
  return (
    <Paper variant="outlined" sx={{ borderRadius: '18px', p: 3, borderColor: (th) => alpha(th.palette.primary.main, 0.25), background: (th) => alpha(th.palette.primary.main, 0.05) }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <Text className="font-semibold" style={{ fontSize: 15 }}>🎯 {b.ruleName}</Text>
        <span className="text-xs opacity-60">{periodLabel(b.period)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <Text className="font-extrabold" style={{ fontSize: 30, lineHeight: 1.1 }}>
          {(b.count || 0).toLocaleString('pt-BR')} <span className="text-base opacity-45 font-semibold">/ {limit.toLocaleString('pt-BR')}</span>
        </Text>
        <Text className="font-bold" style={{ color, fontSize: 18 }}>{pct}%</Text>
      </div>
      <div style={{ height: 10, borderRadius: 999, background: 'rgba(125,125,125,0.18)', overflow: 'hidden', margin: '10px 0' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width .4s ease' }} />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-1">
        <Text className="text-xs opacity-70">
          {over ? 'teto atingido' : `faltam ${remaining.toLocaleString('pt-BR')}`}{showCost ? ` · ${fmtBRL((b.count || 0) * price)} de ${fmtBRL(limit * price)}` : ''}
        </Text>
        <Button size="small" onClick={() => onEdit(b)} sx={{ textTransform: 'none', fontWeight: 600 }}>{b.enabled ? 'Ajustar' : 'Ativar / ajustar'}</Button>
      </div>
      {!b.enabled && <Text className="block text-xs mt-1" style={{ color: '#b54708' }}>Teto desativado — não está travando nada.</Text>}
      {eta && <Text className="block text-xs mt-1" style={{ color: '#b54708' }}>⚠ no ritmo atual, estoura ~{eta}</Text>}
    </Paper>
  )
}

// Escala Semáforo: verde (baixo) → âmbar → vermelho (alto fluxo)
function heatColor(intensity) {
  if (intensity <= 0) return 'rgba(156,163,175,0.12)'
  if (intensity < 0.35) return `rgba(34,197,94,${(0.3 + intensity * 0.7).toFixed(2)})`
  if (intensity < 0.7)  return `rgba(245,158,11,${(0.35 + intensity * 0.65).toFixed(2)})`
  return `rgba(239,68,68,${(0.45 + intensity * 0.55).toFixed(2)})`
}

function HourHeatmap({ heatmap, theme }) {
  const hours = (heatmap && heatmap.hours) || Array(24).fill(0)
  const max = Math.max(...hours, 1)
  return (
    <Paper variant="outlined" sx={{ borderRadius: '16px', p: 2.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary }}>Distribuição por hora</span>
        <span style={{ fontSize: 10, color: theme.palette.text.secondary }}>24h · Horário SP</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24,1fr)', gap: 3, marginBottom: 4 }}>
        {hours.map((v, h) => (
          <div key={h} title={`${String(h).padStart(2,'0')}h: ${v.toLocaleString('pt-BR')} msgs`}
            style={{ height: 28, borderRadius: 3, background: heatColor(v / max), cursor: 'default' }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24,1fr)', gap: 3, marginBottom: 8 }}>
        {hours.map((_, h) => (
          <div key={h} style={{ fontSize: 8, textAlign: 'center', color: theme.palette.text.disabled }}>
            {h % 4 === 0 ? String(h).padStart(2,'0') : ''}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: theme.palette.text.disabled }}>
        {[['rgba(34,197,94,0.6)', 'Baixo'], ['rgba(245,158,11,0.7)', 'Médio'], ['rgba(239,68,68,0.85)', 'Alto fluxo']].map(([bg, label]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: bg, display: 'inline-block' }} />{label}
          </span>
        ))}
      </div>
    </Paper>
  )
}

function VolumeChart({ byDay, theme }) {
  if (!byDay || byDay.length === 0) return null
  const days = byDay.slice(-Math.min(byDay.length, 30))
  const maxTotal = Math.max(...days.map(d => {
    const c = d.counters || {}
    return (c.allowed || 0) + (c.blocked || 0) + (c.delayed || 0) + (c.approvalPending || 0)
  }), 1)
  const LEGEND = [['Liberadas', '#22c55e'], ['Bloqueadas', '#ef4444'], ['Atrasadas', '#f59e0b'], ['Aprovação', '#a855f7']]
  return (
    <Paper variant="outlined" sx={{ borderRadius: '16px', p: 2.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary }}>Volume diário</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {LEGEND.map(([l, c]) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: theme.palette.text.secondary }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: 'inline-block' }} />{l}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
        {days.map((d, i) => {
          const c = d.counters || {}
          const al = c.allowed || 0, bl = c.blocked || 0, dl = c.delayed || 0, ap = c.approvalPending || 0
          const total = al + bl + dl + ap
          const h = Math.max(2, Math.round(76 * (total / maxTotal)))
          return (
            <div key={i} title={`${d.day}: ${total.toLocaleString('pt-BR')}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: h, overflow: 'hidden', borderRadius: '3px 3px 0 0' }}>
              {al > 0 && <div style={{ flex: al, background: '#22c55e' }} />}
              {bl > 0 && <div style={{ flex: bl, background: '#ef4444' }} />}
              {dl > 0 && <div style={{ flex: dl, background: '#f59e0b' }} />}
              {ap > 0 && <div style={{ flex: ap, background: '#a855f7' }} />}
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
        {days.map((d, i) => {
          const show = days.length <= 7 || i % Math.ceil(days.length / 7) === 0
          return (
            <div key={i} style={{ flex: 1, fontSize: 8, textAlign: 'center', color: theme.palette.text.disabled }}>
              {show ? (d.day || '').slice(5) : ''}
            </div>
          )
        })}
      </div>
    </Paper>
  )
}

// `price` = preço do pacote da instância (precoMsgPacote). A economia por regra é
// count × esse preço — mesma base do total ("Economia estimada"). NÃO usamos o
// r.savingsBRL do backend: ele é calculado com o custo global default (messageCostBRL,
// ex.: R$ 0,04) e ignora o pacote contratado da instância.
function RuleAccordion({ ranking, theme, price = PRICE_PER_MESSAGE }) {
  const [open, setOpen] = useState(false)
  if (!ranking || ranking.length === 0) return null
  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', color: theme.palette.text.secondary, fontSize: 12 }}
      >
        <span style={{ fontWeight: 600 }}>Ver regras mais acionadas</span>
        <span style={{ transition: 'transform .3s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block', fontSize: 14 }}>▾</span>
      </button>
      <div style={{ maxHeight: open ? 700 : 0, opacity: open ? 1 : 0, overflow: 'hidden', transition: 'max-height .35s ease, opacity .25s ease' }}>
        <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: theme.palette.text.secondary, marginBottom: 10, marginTop: 4 }}>Regras mais acionadas</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ranking.map((r, i) => (
                <div key={r.ruleId || i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: theme.palette.text.disabled, width: 20, textAlign: 'right', flexShrink: 0 }}>#{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.ruleName}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.palette.error.main }}>{r.count.toLocaleString('pt-BR')}×</div>
                    <div style={{ fontSize: 11, color: theme.palette.text.secondary }}>{fmtBRL((r.count || 0) * price)}</div>
                  </div>
                </div>
              ))}
            </div>
        </div>
      </div>
    </div>
  )
}

// Custo por mensagem cobrado do cliente (hardcoded global)
const PRICE_PER_MESSAGE = 0.06

function BillingCard({ smartFilterAllowed, instanceSuccess, theme, showCost }) {
  // saldoInfo = saldo REAL (recargas − consumo) do backend, apurado contando os
  // envios aceitos pelo provedor (status:success). É a ÚNICA fonte de valor
  // monetário aqui — a cobrança nasce do ENVIO REAL, nunca da decisão do
  // SmartFilter. Sem pacote contratado (saldoInfo null) NÃO exibimos valor:
  // mostramos "sem pacote contratado", em vez de estimar pela contagem de
  // liberadas (decisões), que inflava o "saldo a faturar" muito acima do que de
  // fato saiu/será cobrado.
  const { saldoInfo } = useContext(ApiDataWrapperContext)
  const sfCount = smartFilterAllowed || 0
  const instCount = instanceSuccess != null ? instanceSuccess : null

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 px-4 py-2.5 rounded-xl"
      style={{
        background: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
      }}
    >
      <div className="flex items-center gap-1.5">
        <FilterAltIcon style={{ fontSize: 14, opacity: 0.4 }} />
        <Text className="text-xs opacity-50">Filtradas</Text>
        <Text className="text-sm font-bold">{sfCount.toLocaleString('pt-BR')}</Text>
      </div>
      <div className="flex items-center gap-1.5">
        <DoneAllIcon style={{ fontSize: 14, color: theme.palette.primary.main }} />
        <Text className="text-xs opacity-50">Entregues</Text>
        <Text className="text-sm font-bold">
          {instCount != null ? instCount.toLocaleString('pt-BR') : '—'}
        </Text>
        {instCount != null && instCount !== sfCount && (
          <Text className="text-[10px]" style={{ color: theme.palette.warning.main }}>
            ({Math.abs(instCount - sfCount).toLocaleString('pt-BR')} dif.)
          </Text>
        )}
      </div>
      {showCost && (
        saldoInfo ? (
          <div className="flex items-center gap-1.5 ml-auto">
            <Text className="text-xs opacity-50">Saldo</Text>
            <Text
              className="text-base font-extrabold"
              style={{ color: Number(saldoInfo.saldo) >= 0 ? theme.palette.success.main : theme.palette.error.main }}
            >
              {fmtBRL(saldoInfo.saldo)}
            </Text>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 ml-auto">
            <Text className="text-xs opacity-50">Saldo</Text>
            <Text className="text-sm font-semibold" style={{ color: theme.palette.text.secondary }}>
              Sem pacote contratado
            </Text>
          </div>
        )
      )}
    </div>
  )
}

function Overview({ stats, theme, budget, costBRL, mode, ranking, heatmap, instanceStats, usaLinhaDeCredito, periodLabel, reportLoading, onNewBudget, onEditBudget }) {
  const { precoMsgPacote } = useContext(ApiDataWrapperContext)
  const t = (stats && stats.totals) || {}
  const byDay = (stats && stats.byDay) || []
  const savings = (t.blocked || 0) * (precoMsgPacote || PRICE_PER_MESSAGE)
  const budgets = budget || []
  const isSimulation = mode === 'simulation' || mode === 'shadow'
  const savingsLabel = isSimulation ? 'Economia potencial' : 'Economia estimada'
  const savingsSubtitle = isSimulation
    ? `${(t.blocked || 0).toLocaleString('pt-BR')} msgs seriam bloqueadas — ative para garantir a economia`
    : `${(t.blocked || 0).toLocaleString('pt-BR')} mensagens bloqueadas que não viraram custo`

  return (
    <div>
      {/* Barra de saldo no topo */}
      <BillingCard
        smartFilterAllowed={t.allowed || 0}
        instanceSuccess={instanceStats?.success}
        theme={theme}
        showCost={usaLinhaDeCredito}
      />

      {budgets.length > 0 ? (
        <div className="flex flex-col gap-3 mb-5">
          {budgets.map((b) => <BudgetMeter key={b.ruleId} b={b} onEdit={onEditBudget} showCost={usaLinhaDeCredito} />)}
        </div>
      ) : (
        <Button
          fullWidth onClick={onNewBudget}
          sx={{ mb: 2.5, py: 1.75, borderRadius: '16px', border: '1px dashed', borderColor: 'divider', textTransform: 'none', fontWeight: 600, color: 'text.secondary', '&:hover': { borderColor: 'primary.main', bgcolor: (th) => alpha(th.palette.primary.main, 0.04) } }}
        >
          🎯 Definir um teto de envios no período (trava o gasto)
        </Button>
      )}

      <Paper variant="outlined" sx={{ borderRadius: '18px', p: 3, mb: 3, borderColor: (th) => alpha(th.palette.success.main, 0.3), background: (th) => alpha(th.palette.success.main, 0.06) }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Text className="block text-xs uppercase opacity-60" style={{ letterSpacing: '0.08em' }}>{savingsLabel}{periodLabel ? ` · ${periodLabel}` : ''}</Text>
            <Text className="block font-extrabold mt-1" style={{ fontSize: 36, lineHeight: 1.05, color: theme.palette.success.main }}>{fmtBRL(savings)}</Text>
          </div>
          <Text className="text-sm opacity-70 max-w-[260px] sm:text-right">{savingsSubtitle}</Text>
        </div>
        <RuleAccordion ranking={ranking} theme={theme} price={precoMsgPacote || PRICE_PER_MESSAGE} />
      </Paper>

      {/* "Liberadas" = decisões ALLOW do filtro, NÃO envios reais. Envio real
          (status:success) aparece em "Entregues" no BillingCard acima. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <KpiCard label="Recebidas" value={t.inbound || 0} accent={theme.palette.text.primary} loading={reportLoading} icon={<MoveToInboxIcon style={{ fontSize: 22 }} />} />
        <KpiCard label="Liberadas" value={t.allowed || 0} accent="#059669" loading={reportLoading} icon={<CheckCircleIcon style={{ fontSize: 22 }} />} />
        <KpiCard label="Bloqueadas" value={t.blocked || 0} accent="#eb3c3c" loading={reportLoading} icon={<BlockIcon style={{ fontSize: 22 }} />} />
        <KpiCard label="Atrasadas" value={t.delayed || 0} accent="#f59e0b" loading={reportLoading} icon={<ScheduleIcon style={{ fontSize: 22 }} />} />
        <KpiCard label="Aprovação" value={t.approvalPending || 0} accent={theme.palette.secondary.main} loading={reportLoading} icon={<PendingActionsIcon style={{ fontSize: 22 }} />} />
      </div>

      {byDay.length > 0 && (() => {
        // Eixo derivado do PRÓPRIO byDay (fonte única) e preenchido entre o
        // primeiro e o último dia com dado — nunca fica um eixo de um período
        // contra os dados de outro. byDay já vem ordenado por dia (BRT) do backend.
        const dayList = daysBetween(byDay[0].day, byDay[byDay.length - 1].day)
        const byDayMap = {}
        byDay.forEach((d) => { byDayMap[d.day] = d.counters || {} })
        return (
          <ReportChart
            title="Decisões por dia"
            periodLabel={periodLabel}
            categories={dayList}
            series={[
              { name: 'Liberadas', color: '#059669', data: dayList.map((day) => (byDayMap[day] && byDayMap[day].allowed) || 0) },
              { name: 'Bloqueadas', color: '#eb3c3c', data: dayList.map((day) => (byDayMap[day] && byDayMap[day].blocked) || 0) },
            ]}
            unitLabel="mensagens"
            exportFilename="relatorio-smartfilter"
          />
        )
      })()}
      {byDay.length > 0 && (
        <div className="mt-4">
          <HourHeatmap heatmap={heatmap} theme={theme} />
        </div>
      )}

      {!reportLoading && byDay.length === 0 && (
        <Text className="block mt-5 text-sm opacity-60">
          Sem dados no período selecionado. Os números aparecem conforme as mensagens passam pelo filtro.
        </Text>
      )}
    </div>
  )
}

function SettingsTab({ settings, setSettings, onSave, onReset }) {
  const set = (k, v) => setSettings({ ...settings, [k]: v })
  return (
    <div className="space-y-6 max-w-xl">
    <Paper variant="outlined" sx={{ borderRadius: '16px', p: 3 }}>
      <div className="space-y-4">
      <FormControl fullWidth size="small">
        <InputLabel>Modo de operação</InputLabel>
        <Select label="Modo de operação" value={settings.mode || 'off'} onChange={(e) => set('mode', e.target.value)}>
          {Object.keys(MODE_LABELS).map((m) => <MenuItem key={m} value={m}>{MODE_LABELS[m]}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Estratégia</InputLabel>
        <Select label="Estratégia" value={settings.filterMode || 'blacklist'} onChange={(e) => set('filterMode', e.target.value)}>
          <MenuItem value="blacklist">Passar tudo, exceto o que as regras bloquearem</MenuItem>
          <MenuItem value="whitelist">Enviar só o que as regras permitirem (lista-branca)</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Resposta ao bloquear</InputLabel>
        <Select label="Resposta ao bloquear" value={settings.blockResponseMode || 'transparent'} onChange={(e) => set('blockResponseMode', e.target.value)}>
          <MenuItem value="transparent">Transparente (ERP não percebe o bloqueio)</MenuItem>
          <MenuItem value="explicit">Explícita (ERP recebe sinal de bloqueio)</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth size="small" type="number" label="Janela de duplicados (minutos)"
        value={Math.round((settings.dedupWindowMs || 0) / 60000)}
        onChange={(e) => set('dedupWindowMs', Math.max(0, Number(e.target.value)) * 60000)}
      />

      <TextField
        fullWidth size="small" type="number" label="Limite de mensagens por número/dia (0 = ilimitado)"
        value={settings.dailyRecipientLimit || 0}
        onChange={(e) => set('dailyRecipientLimit', Math.max(0, Number(e.target.value)))}
      />

      <TextField
        fullWidth size="small" label="Sistema de origem"
        placeholder="Ex.: Meu ERP, Sistema Interno"
        value={settings.defaultSource || ''}
        onChange={(e) => set('defaultSource', e.target.value)}
        helperText="Usado quando a origem não é detectada automaticamente. As decisões saem marcadas com este nome."
      />

        <Button variant="contained" disableElevation onClick={onSave} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>Salvar configurações</Button>
      </div>
    </Paper>

    <Paper variant="outlined" sx={{ borderRadius: '16px', p: 3, borderColor: 'error.main', borderStyle: 'dashed' }}>
      <Text className="text-sm font-semibold mb-1 block">Zona de perigo</Text>
      <Text className="text-xs opacity-70 mb-3 block">
        Resetar remove todos os dados acumulados (inadimplência, bloqueios, decisões, contadores).
        As regras e configurações são preservadas.
      </Text>
      <Button
        variant="outlined" color="error" size="small" startIcon={<RestartAltIcon />}
        onClick={onReset}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}
      >
        Resetar dados do SmartFilter
      </Button>
    </Paper>
    </div>
  )
}

const CATEGORY_META = {
  cobranca: { label: 'Cobrança inteligente', icon: '💰', order: 1 },
  'anti-desperdicio': { label: 'Anti-desperdício', icon: '🛡️', order: 2 },
  qualidade: { label: 'Qualidade da mensagem', icon: '🧩', order: 3 },
  horario: { label: 'Horário e timing', icon: '🕐', order: 4 },
  garantia: { label: 'Garantias (sempre passa)', icon: '✅', order: 5 }
}

const TEMPLATE_ICON = {
  'teto-envios-periodo': '🎯',
  'parar-cobranca-inadimplencia-antiga': '🔕',
  'so-com-vencimento': '📅', 'variavel-nao-preenchida': '🧩', 'anti-dedup': '🛡️', 'lista-supressao': '🚫',
  'max-por-dia': '🔇', 'intervalo-minimo': '⏳', 'nao-cobrar-cedo': '⏰', 'janela-d2-d5': '🗓️',
  'so-vencidas': '📛', 'valor-minimo': '🪙', 'cobranca-alta-aprovacao': '💰', 'nao-classificada-aprovacao': '❓',
  'nada-de-madrugada': '🌙', 'promo-horario-comercial': '📣', 'sem-domingo': '📵',
  'desbloqueio-sempre': '🔓', 'vip-sempre': '⭐'
}

const FEATURED = ['teto-envios-periodo', 'parar-cobranca-inadimplencia-antiga', 'so-com-vencimento', 'variavel-nao-preenchida', 'anti-dedup', 'lista-supressao']

function RecipeCard({ t, installed, onImport, featured }) {
  return (
    <Paper
      variant="outlined" className="flex flex-col"
      sx={{ borderRadius: '16px', p: 2.5, transition: 'box-shadow .2s, transform .2s', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' }, ...(featured && { borderColor: 'primary.main' }) }}
    >
      <div className="flex items-center justify-between">
        <span style={{ fontSize: '1.15rem' }}>{TEMPLATE_ICON[t.code] || '•'}</span>
        <OutcomeChip type={t.action?.type} />
      </div>
      <Text className="block font-semibold mt-2" style={{ fontSize: 14, lineHeight: 1.25 }}>{t.name}</Text>
      <Text className="block text-xs opacity-70 mt-1 flex-1" style={{ lineHeight: 1.35 }}>{t.description}</Text>
      <div className="mt-3 flex items-center justify-between">
        {installed
          ? <span className="text-xs font-semibold" style={{ color: '#16a34a' }}>✓ Instalada</span>
          : <span />}
        <Button size="small" variant={installed ? 'text' : 'outlined'} startIcon={installed ? null : <AddTaskIcon />} onClick={() => onImport(t)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>
          {installed ? 'Instalar de novo' : 'Instalar'}
        </Button>
      </div>
    </Paper>
  )
}

function TemplatesTab({ templates, onImport, installedCodes }) {
  const codes = installedCodes || new Set()
  const featured = FEATURED.map((c) => templates.find((t) => t.code === c)).filter(Boolean)
  const byCat = {}
  templates.forEach((t) => { const c = t.category || 'outros'; (byCat[c] = byCat[c] || []).push(t) })
  const cats = Object.keys(byCat).sort((a, b) => ((CATEGORY_META[a] || {}).order || 99) - ((CATEGORY_META[b] || {}).order || 99))

  if (!templates.length) {
    return <Text className="block text-sm opacity-60">Nenhuma receita disponível.</Text>
  }

  return (
    <div>
      {featured.length > 0 && (
        <div>
          <div className="flex items-center gap-2 font-semibold mb-2"><span style={{ color: '#f5a623' }}>⭐</span> Essenciais para provedor</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {featured.map((t) => <RecipeCard key={t.code} t={t} featured installed={codes.has(t.code)} onImport={onImport} />)}
          </div>
        </div>
      )}
      {cats.map((c) => {
        const meta = CATEGORY_META[c] || { label: c, icon: '•' }
        return (
          <div key={c} className="mt-6">
            <div className="flex items-center gap-2 font-semibold mb-2">
              <span>{meta.icon}</span> {meta.label}
              <span className="text-xs font-semibold" style={{ color: '#6b7280', background: '#f2f4f7', borderRadius: 999, padding: '1px 8px' }}>{byCat[c].length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {byCat[c].map((t) => <RecipeCard key={t.code} t={t} installed={codes.has(t.code)} onImport={onImport} />)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function RulesTab({ rules, onToggle, onDelete, onNew, onEdit }) {
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={onNew} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>
          Nova regra
        </Button>
      </div>
      {!rules.length ? (
        <Text className="block text-sm opacity-60">
          Nenhuma regra ainda. Crie uma com "Nova regra" ou instale uma receita na aba "Receitas".
        </Text>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ativa</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Prioridade</TableCell>
                <TableCell>Ação</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r._id}>
                  <TableCell><Switch size="small" checked={!!r.enabled} onChange={() => onToggle(r._id)} /></TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{labelSource(r.source)}</TableCell>
                  <TableCell>{r.priority}</TableCell>
                  <TableCell><OutcomeChip type={r.action?.type} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => onEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton size="small" color="error" onClick={() => onDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}

function ApprovalsTab({ approvals, onDecide }) {
  if (!approvals.length) {
    return <Text className="block text-sm opacity-60">Nenhuma mensagem aguardando aprovação.</Text>
  }
  return (
    <div className="space-y-3">
      {approvals.map((a) => (
        <Paper key={a._id} variant="outlined" className="p-4" sx={{ borderRadius: '16px' }}>
          <div className="flex items-center justify-between">
            <Text className="font-semibold">{a.phone}</Text>
            <Chip size="small" color={DECISION_COLORS.REQUIRE_APPROVAL} label={labelReason(a.reason) || 'Aprovação'} />
          </div>
          <Text className="block text-sm opacity-80 mt-2 whitespace-pre-wrap">{a.message}</Text>
          <div className="mt-3 space-x-2">
            <Button size="small" variant="contained" disableElevation color="success" startIcon={<CheckCircleIcon />} onClick={() => onDecide(a._id, true)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>
              Aprovar e enviar
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => onDecide(a._id, false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}>
              Rejeitar
            </Button>
          </div>
        </Paper>
      ))}
    </div>
  )
}
